import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap,
  Mail,
  Lock,
  User,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { api } from './convex/_generated/api';
import { useAction } from 'convex/react';
import { toast } from 'sonner';

export default function Auth2aitravel() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const createUser = useAction(api.mfa.createUsers);
  const loginUser = useAction(api.mfa.loginUser);
  const sendOTP = useAction(api.mfa.sendOTP);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const user = await loginUser({ email, password });

        if (!user) {
          toast.error('User not found');
        } else {
          toast.success(`Welcome back, ${user.fullName || user.email}`);

          if (!user.mfaEnabled) {
            navigate('/mfa-setup', {
              state: {
                userId: user.userId,
                email: user.email,
              },
            });
          } else {
            await sendOTP({
              userId: user.userId,
              email: user.email,
            });

            navigate('/mfa-verify', {
              state: {
                userId: user.userId,
                email: user.email,
              },
            });
          }
        }
      } else {
        if (!fullName.trim()) {
          toast.error('Please enter your full name');
          return;
        }

        const user = await createUser({
          email,
          password,
          fullName,
        });

        toast.success('Account created!');

        if (!user.mfaEnabled) {
          navigate('/mfa-setup', {
            state: {
              userId: user.userId,
              email: user.email,
            },
          });
        } else {
          navigate('/mfa-verify', {
            state: {
              userId: user.userId,
              email: user.email,
            },
          });
        }
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.glow1} />
      <div style={S.glow2} />

      {/* HEADER */}
      <header style={S.header}>
        <div style={S.logoWrap}>
          <div style={S.logoIcon}>
            <Zap size={18} color="#fff" />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={S.logoText}>AITravel</span>
            <span style={S.logoBeta}>BETA</span>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <div style={S.center}>
        <div style={S.card}>
          {/* TOP ICON */}
          <div style={S.heroIcon}>
            <Zap size={34} color="#fff" />
          </div>

          <h1 style={S.title}>
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>

          <p style={S.subtitle}>
            {isLogin
              ? 'Sign in to continue your secure workspace experience.'
              : 'Create your account and start saying what matters.'}
          </p>

          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            {!isLogin && (
              <div style={S.fieldWrap}>
                <label style={S.label}>Full Name</label>

                <div style={S.inputWrap}>
                  <User size={18} style={S.inputIcon} />

                  <input
                    type="text"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    style={S.input}
                    required
                  />
                </div>
              </div>
            )}

            <div style={S.fieldWrap}>
              <label style={S.label}>Email Address</label>

              <div style={S.inputWrap}>
                <Mail size={18} style={S.inputIcon} />

                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={S.input}
                  required
                />
              </div>
            </div>

            <div style={S.fieldWrap}>
              <label style={S.label}>Password</label>

              <div style={S.inputWrap}>
                <Lock size={18} style={S.inputIcon} />

                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={S.input}
                  required
                  minLength={8}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                ...S.btn,
                opacity: loading ? 0.75 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <Loader2
                  size={20}
                  style={{
                    animation: 'spin 1s linear infinite',
                  }}
                />
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* TOGGLE */}
          <div style={S.toggleWrap}>
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              style={S.toggleBtn}
            >
              {isLogin
                ? "Don't have an account? Sign up"
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    width: '100%',
    minHeight: '100vh',
    background:
      'radial-gradient(circle at top left, rgba(124,58,237,0.25), transparent 30%), radial-gradient(circle at bottom right, rgba(74,222,128,0.18), transparent 30%), linear-gradient(135deg, #0B1020 0%, #111827 45%, #0F172A 100%)',
    fontFamily: "'Inter', sans-serif",
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
  },

  glow1: {
    position: 'fixed',
    top: -120,
    right: -120,
    width: 420,
    height: 420,
    background: 'rgba(139,92,246,0.28)',
    borderRadius: '50%',
    filter: 'blur(140px)',
    pointerEvents: 'none',
    zIndex: 0,
  },

  glow2: {
    position: 'fixed',
    bottom: -120,
    left: -120,
    width: 420,
    height: 420,
    background: 'rgba(16,185,129,0.16)',
    borderRadius: '50%',
    filter: 'blur(140px)',
    pointerEvents: 'none',
    zIndex: 0,
  },

  header: {
    width: '100%',
    height: 72,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 42px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(10,15,30,0.55)',
    backdropFilter: 'blur(16px)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxSizing: 'border-box',
  },

  logoWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },

  logoIcon: {
    width: 40,
    height: 40,
    borderRadius: 14,
    background: 'linear-gradient(135deg, #8B5CF6, #22C55E)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 10px 30px rgba(124,58,237,0.35)',
  },

  logoText: {
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: '-0.04em',
    color: '#ffffff',
  },

  logoBeta: {
    fontSize: 10,
    fontWeight: 700,
    color: '#A78BFA',
    border: '1px solid rgba(167,139,250,0.5)',
    borderRadius: 999,
    padding: '4px 10px',
    letterSpacing: '0.08em',
    background: 'rgba(139,92,246,0.12)',
  },

  center: {
    width: '100%',
    minHeight: 'calc(100vh - 72px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    boxSizing: 'border-box',
    position: 'relative',
    zIndex: 2,
  },

  card: {
    width: '100%',
    maxWidth: 460,
    padding: '42px',
    borderRadius: 32,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)',
    backdropFilter: 'blur(22px)',
    boxShadow:
      '0 20px 80px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },

  heroIcon: {
    width: 72,
    height: 72,
    borderRadius: 22,
    margin: '0 auto 24px',
    background: 'linear-gradient(135deg, #8B5CF6, #22C55E)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 20px 50px rgba(124,58,237,0.4)',
  },

  title: {
    fontSize: 34,
    fontWeight: 800,
    textAlign: 'center',
    margin: '0 0 12px',
    letterSpacing: '-0.04em',
    color: '#ffffff',
  },

  subtitle: {
    fontSize: 15,
    lineHeight: 1.7,
    textAlign: 'center',
    color: 'rgba(255,255,255,0.62)',
    marginBottom: 34,
  },

  fieldWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },

  label: {
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.72)',
    marginLeft: 4,
  },

  inputWrap: {
    position: 'relative',
  },

  inputIcon: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'rgba(255,255,255,0.35)',
  },

  input: {
    width: '100%',
    height: 56,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.05)',
    padding: '0 16px 0 48px',
    color: '#fff',
    fontSize: 15,
    fontWeight: 500,
    outline: 'none',
    transition: 'all 0.25s ease',
    boxSizing: 'border-box',
    backdropFilter: 'blur(10px)',
  },

  btn: {
    width: '100%',
    height: 56,
    borderRadius: 18,
    border: 'none',
    marginTop: 8,
    background: 'linear-gradient(135deg, #8B5CF6, #22C55E)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    transition: 'all 0.25s ease',
    boxShadow: '0 16px 40px rgba(124,58,237,0.35)',
  },

  toggleWrap: {
    marginTop: 26,
    textAlign: 'center',
  },

  toggleBtn: {
    border: 'none',
    background: 'transparent',
    color: 'rgba(255,255,255,0.58)',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: '0.2s ease',
  },
};
